import React from "react";
import {
  getPaymentAmount,
  getSuccessfulPayments,
  hasSuccessfulPaymentRecord,
} from "../paymentCalculations";

export const TotalAmountSummarySection = ({
  formData,
  registrationFee,
  accompanyingPersonFee,
  focusedSessionFees,
  conveyanceFeePercentage,
  calculateOutstandingAmounts,
}) => {
  if (!registrationFee && !hasSuccessfulPaymentRecord(formData.payment)) {
    return null;
  }

  const payments = formData.payment || [];
  const successfulPayments = getSuccessfulPayments(payments);
  const regFee = parseFloat(registrationFee?.rate || 0);
  const currency =
    registrationFee?.currency ||
    successfulPayments[0]?.currency ||
    (formData.country === "India" ? "INR" : "USD");
  const currencySymbol =
    currency === "INR" ? "₹" : currency === "USD" ? "₹" : currency;
  const existingAdditionalFees = formData.additionalFees || [];
  const registrationPaid = formData.paid ? regFee : 0;

  const paidAccompanyingFees = existingAdditionalFees
    .filter(
      (fee) =>
        accompanyingPersonFee &&
        fee.feeTypeId === accompanyingPersonFee.id &&
        fee.paymentId !== null &&
        fee.paymentId !== undefined
    )
    .reduce((sum, fee) => {
      let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
      if (itemRate === 0 && accompanyingPersonFee) {
        itemRate = parseFloat(accompanyingPersonFee.rate || 0);
      }
      return sum + itemRate;
    }, 0);

  const paidFocusedFees = existingAdditionalFees
    .filter(
      (fee) =>
        fee.paymentId !== null &&
        fee.paymentId !== undefined &&
        (!accompanyingPersonFee || fee.feeTypeId !== accompanyingPersonFee.id)
    )
    .reduce((sum, fee) => {
      let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
      if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
        itemRate = parseFloat(focusedSessionFees[fee.feeTypeId].rate || 0);
      }
      return sum + itemRate;
    }, 0);

  let totalPaidAmount = 0;
  let paidConvenienceFee = 0;

  if (successfulPayments.length > 0 && successfulPayments[0].breakdown) {
    totalPaidAmount = successfulPayments[0].breakdown.reduce((sum, item) => {
      const amount = parseFloat(item.amount || 0);
      if (item.type === "CONVENIENCE_FEE") {
        paidConvenienceFee += amount;
      }
      return sum + amount;
    }, 0);
    console.log("Using payment breakdown from DB:", totalPaidAmount);
  } else if (successfulPayments.length > 0) {
    totalPaidAmount = successfulPayments.reduce(
      (sum, payment) => sum + getPaymentAmount(payment),
      0
    );
    paidConvenienceFee = 0;
    console.log("Using payment.amount records from DB:", totalPaidAmount);
  } else {
    const totalPaidAmountBeforeFee =
      registrationPaid + paidAccompanyingFees + paidFocusedFees;
    paidConvenienceFee =
      conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
        ? totalPaidAmountBeforeFee * (conveyanceFeePercentage / 100)
        : 0;
    totalPaidAmount = totalPaidAmountBeforeFee + paidConvenienceFee;
    console.log("Using calculated paid amount (no breakdown in DB):", totalPaidAmount);
  }

  const outstandingSummary = calculateOutstandingAmounts();
  const {
    registrationOutstanding,
    outstandingItems,
    outstandingFromAdditionalFees,
    newAccompanyingCount,
    newAccompanyingOutstanding,
    newFocusedSessionIds,
    newFocusedOutstanding,
    totalOutstandingBeforeFee,
    conveyanceFeeAmount,
    totalOutstanding,
  } = outstandingSummary;
  const convenienceFee = conveyanceFeeAmount;
  const grandTotal = totalPaidAmount + totalOutstanding;
  const payableNow = totalOutstanding;
  const hasRegisteredPaymentHistory =
    formData.id &&
    (formData.paid || successfulPayments.length > 0) &&
    totalPaidAmount > 0;

  console.log("Outstanding items from additionalFees:", outstandingItems, "Total outstanding:", outstandingFromAdditionalFees);
  console.log("Total Amount Calculation:", {
    "formData.paid": formData.paid,
    registrationFee: regFee,
    registrationPaid,
    registrationOutstanding,
    existingAdditionalFees,
    outstandingFromAdditionalFees,
    newAccompanyingCount,
    newAccompanyingOutstanding,
    newFocusedSessionIds,
    newFocusedOutstanding,
    totalPaidAmount,
    totalOutstanding,
    grandTotal,
  });

  const formatPaymentDescription = (description) =>
    (description || "Payment").replace(/^Focuss?ed Session\s*-\s*/i, "");

  const renderRegisteredPaymentHistoryItems = () => {
    const fullBreakdown =
      successfulPayments.length > 0 && successfulPayments[0].breakdown
        ? successfulPayments[0].breakdown
        : [];
    let displayItems = [];

    console.log("Registered user payment debug:");
    console.log("  totalPaidAmount:", totalPaidAmount);
    console.log("  paidConvenienceFee:", paidConvenienceFee);
    console.log("  fullBreakdown from DB:", fullBreakdown);
    console.log("  registrationFee:", registrationFee);
    console.log("  paidFocusedFees:", paidFocusedFees);
    console.log("  paidAccompanyingFees:", paidAccompanyingFees);

    if (fullBreakdown.length > 0) {
      displayItems = fullBreakdown.filter(
        (item) => item.type !== "CONVEYANCE_FEE"
      );
    } else if (successfulPayments.length > 0) {
      displayItems = successfulPayments
        .filter((payment) => getPaymentAmount(payment) > 0)
        .map((payment) => ({
          type: payment.paymentType || "PAYMENT",
          description:
            payment.description ||
            payment.modeOfPayment ||
            payment.paymentType ||
            "Payment",
          amount: getPaymentAmount(payment),
          currency: payment.currency || currency,
        }));
    } else {
      if (registrationFee && registrationFee.rate && registrationPaid > 0) {
        displayItems.push({
          type: "BASE_FEE",
          description: `Registration Fee - ${
            registrationFee.feeCategory?.value || "Standard"
          }`,
          amount: registrationPaid,
          currency,
        });
      }
      if (paidFocusedFees > 0) {
        displayItems.push({
          type: "FOCUSED_SESSIONS",
          description: "Workshops",
          amount: paidFocusedFees,
          currency,
        });
      }
      if (paidAccompanyingFees > 0) {
        displayItems.push({
          type: "ACCOMPANYING_PERSONS",
          description: "Accompanying Persons",
          amount: paidAccompanyingFees,
          currency,
        });
      }
    }

    console.log("  displayItems (to render):", displayItems);

    return (
      <>
        {displayItems.map((item, index) => (
          <div
            key={index}
            className="summary-row"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) max-content",
              columnGap: "16px",
              alignItems: "start",
            }}
          >
            <span
              className="summary-label"
              style={{
                fontSize: "0.95em",
                fontWeight: "400",
                color: "#28a745",
                minWidth: 0,
                overflowWrap: "anywhere",
                textAlign: "left",
              }}
            >
              {formatPaymentDescription(item.description)}:
            </span>
            <span
              className="summary-value"
              style={{
                fontSize: "0.95em",
                fontWeight: "400",
                color: "#28a745",
                whiteSpace: "nowrap",
                textAlign: "right",
              }}
            >
              {currencySymbol}
              {parseFloat(item.amount || 0).toFixed(2)} ({item.currency || currency})
            </span>
          </div>
        ))}
        {paidConvenienceFee > 0 &&
          conveyanceFeePercentage !== null &&
          conveyanceFeePercentage > 0 && (
            <div
              style={{
                marginTop: "5px",
                paddingTop: "5px",
                borderTop: "1px solid #dee2e6",
              }}
            >
              <div className="summary-row">
                <span
                  className="summary-label"
                  style={{
                    fontSize: "0.95em",
                    fontWeight: "400",
                    color: "#28a745",
                    fontStyle: "italic",
                  }}
                >
                  Conveyance Fee ({conveyanceFeePercentage}%):
                </span>
                <span
                  className="summary-value"
                  style={{
                    fontSize: "0.95em",
                    fontWeight: "400",
                    color: "#28a745",
                    fontStyle: "italic",
                  }}
                >
                  {currencySymbol}
                  {paidConvenienceFee.toFixed(2)} ({currency})
                </span>
              </div>
            </div>
          )}
      </>
    );
  };

  return (
    <div
      className="summary-section"
      style={{
        backgroundColor: "#f8f9fa",
        padding: "15px",
        marginTop: "10px",
        borderRadius: "5px",
      }}
    >
      <h4 style={{ marginBottom: "15px" }}>Total Amount</h4>

      {hasRegisteredPaymentHistory && (
        <>
          {renderRegisteredPaymentHistoryItems()}
          <div
            className="summary-row"
            style={{
              marginTop: "8px",
              paddingTop: "8px",
              borderTop: "2px solid #dee2e6",
            }}
          >
            <span
              className="summary-label"
              style={{ fontSize: "1em", fontWeight: "500", color: "#28a745" }}
            >
              ✓ Payment History (Already Paid):
            </span>
            <span
              className="summary-value"
              style={{ fontSize: "1.1em", fontWeight: "bold", color: "#28a745" }}
            >
              {currencySymbol}
              {totalPaidAmount.toFixed(2)} ({currency})
            </span>
          </div>
          {totalOutstanding > 0 && (
            <div className="summary-row">
              <span
                className="summary-label"
                style={{ fontSize: "1em", fontWeight: "500", color: "#666" }}
              >
                Outstanding Amount (New Items):
              </span>
              <span
                className="summary-value"
                style={{ fontSize: "1em", fontWeight: "500", color: "#666" }}
              >
                {currencySymbol}
                {totalOutstandingBeforeFee.toFixed(2)} ({currency})
              </span>
            </div>
          )}
        </>
      )}

      {!hasRegisteredPaymentHistory &&
        totalPaidAmount > 0 &&
        totalOutstanding > 0 && (
          <>
            <div className="summary-row">
              <span
                className="summary-label"
                style={{ fontSize: "1em", fontWeight: "500", color: "#28a745" }}
              >
                Paid Amount (incl. 2.9% fee):
              </span>
              <span
                className="summary-value"
                style={{ fontSize: "1.1em", fontWeight: "bold", color: "#28a745" }}
              >
                {currencySymbol}
                {totalPaidAmount.toFixed(2)} ({currency})
              </span>
            </div>
            <div className="summary-row">
              <span
                className="summary-label"
                style={{ fontSize: "1em", fontWeight: "500", color: "#666" }}
              >
                Outstanding (before fee):
              </span>
              <span
                className="summary-value"
                style={{ fontSize: "1em", fontWeight: "500", color: "#666" }}
              >
                {currencySymbol}
                {totalOutstandingBeforeFee.toFixed(2)} ({currency})
              </span>
            </div>
            {conveyanceFeePercentage !== null && conveyanceFeePercentage > 0 && (
              <div className="summary-row">
                <span
                  className="summary-label"
                  style={{
                    fontSize: "0.95em",
                    fontWeight: "400",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  Conveyance Fee ({conveyanceFeePercentage}%):
                </span>
                <span
                  className="summary-value"
                  style={{
                    fontSize: "0.95em",
                    fontWeight: "400",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  {currencySymbol}
                  {convenienceFee.toFixed(2)} ({currency})
                </span>
              </div>
            )}
            <div className="summary-row">
              <span
                className="summary-label"
                style={{ fontSize: "1em", fontWeight: "500", color: "#dc3545" }}
              >
                Outstanding Amount:
              </span>
              <span
                className="summary-value"
                style={{ fontSize: "1.1em", fontWeight: "bold", color: "#dc3545" }}
              >
                {currencySymbol}
                {totalOutstanding.toFixed(2)} ({currency})
              </span>
            </div>
            <div
              className="summary-row"
              style={{
                marginTop: "10px",
                paddingTop: "10px",
                borderTop: "2px solid #dee2e6",
              }}
            >
              <span
                className="summary-label"
                style={{ fontSize: "1.1em", fontWeight: "bold" }}
              >
                Total Payable Now:
              </span>
              <span
                className="summary-value"
                style={{ fontSize: "1.2em", fontWeight: "bold", color: "#007bff" }}
              >
                {currencySymbol}
                {payableNow.toFixed(2)} ({currency})
              </span>
            </div>
            {!formData.paid && (
              <div className="summary-row">
                <span className="summary-label">
                  Cumulative Paid + Outstanding:
                </span>
                <span className="summary-value">
                  {currencySymbol}
                  {grandTotal.toFixed(2)} ({currency})
                </span>
              </div>
            )}
          </>
        )}

      {totalPaidAmount > 0 && totalOutstanding === 0 && (
        <>
          {paidConvenienceFee > 0 && (
            <>
              <div className="summary-row">
                <span
                  className="summary-label"
                  style={{ fontSize: "1em", fontWeight: "500", color: "#28a745" }}
                >
                  Subtotal:
                </span>
                <span
                  className="summary-value"
                  style={{ fontSize: "1em", fontWeight: "500", color: "#28a745" }}
                >
                  {currencySymbol}
                  {(totalPaidAmount - paidConvenienceFee).toFixed(2)} ({currency})
                </span>
              </div>
              {conveyanceFeePercentage !== null &&
                conveyanceFeePercentage > 0 && (
                  <div className="summary-row">
                    <span
                      className="summary-label"
                      style={{
                        fontSize: "0.95em",
                        fontWeight: "400",
                        color: "#28a745",
                        fontStyle: "italic",
                      }}
                    >
                      Conveyance Fee ({conveyanceFeePercentage}%):
                    </span>
                    <span
                      className="summary-value"
                      style={{
                        fontSize: "0.95em",
                        fontWeight: "400",
                        color: "#28a745",
                        fontStyle: "italic",
                      }}
                    >
                      {currencySymbol}
                      {paidConvenienceFee.toFixed(2)} ({currency})
                    </span>
                  </div>
                )}
            </>
          )}
          <div
            className="summary-row"
            style={{
              marginTop: "10px",
              paddingTop: "10px",
              borderTop: paidConvenienceFee > 0 ? "2px solid #dee2e6" : "none",
            }}
          >
            <span
              className="summary-label"
              style={{ fontSize: "1.1em", fontWeight: "bold", color: "#28a745" }}
            >
              Total Amount:
            </span>
            <span
              className="summary-value"
              style={{ fontSize: "1.2em", fontWeight: "bold", color: "#28a745" }}
            >
              {currencySymbol}
              {totalPaidAmount.toFixed(2)} ({currency}) ✓ Fully Paid
            </span>
          </div>
        </>
      )}

      {totalPaidAmount === 0 && totalOutstanding > 0 && (
        <>
          <div className="summary-row">
            <span
              className="summary-label"
              style={{ fontSize: "1em", fontWeight: "500", color: "#666" }}
            >
              Subtotal:
            </span>
            <span
              className="summary-value"
              style={{ fontSize: "1em", fontWeight: "500", color: "#666" }}
            >
              {currencySymbol}
              {totalOutstandingBeforeFee.toFixed(2)} ({currency})
            </span>
          </div>
          {conveyanceFeePercentage !== null && conveyanceFeePercentage > 0 && (
            <div className="summary-row">
              <span
                className="summary-label"
                style={{
                  fontSize: "0.95em",
                  fontWeight: "400",
                  color: "#666",
                  fontStyle: "italic",
                }}
              >
                Conveyance Fee ({conveyanceFeePercentage}%):
              </span>
              <span
                className="summary-value"
                style={{
                  fontSize: "0.95em",
                  fontWeight: "400",
                  color: "#666",
                  fontStyle: "italic",
                }}
              >
                {currencySymbol}
                {convenienceFee.toFixed(2)} ({currency})
              </span>
            </div>
          )}
          <div
            className="summary-row"
            style={{
              marginTop: "10px",
              paddingTop: "10px",
              borderTop: "2px solid #dee2e6",
            }}
          >
            <span
              className="summary-label"
              style={{ fontSize: "1.1em", fontWeight: "bold", color: "#dc3545" }}
            >
              Outstanding Amount:
            </span>
            <span
              className="summary-value"
              style={{ fontSize: "1.2em", fontWeight: "bold", color: "#dc3545" }}
            >
              {currencySymbol}
              {totalOutstanding.toFixed(2)} ({currency})
            </span>
          </div>



          
        </>
      )}



      
    </div>
  );
};
